import { createAction } from '@reduxjs/toolkit';

import type { URLString } from '@bugbug/core/types/aliases';
import type { Group } from '@bugbug/core/types/groups';
import type { PlaybackCursorPosition } from '@bugbug/core/types/playback';
import type { Step } from '@bugbug/core/types/steps';
import type { CurrentTestGrouping, Test, TestRun } from '@bugbug/core/types/tests';
import type { User } from '@bugbug/core/types/users';
import type { Maybe } from '@bugbug/core/types/utils';

const testUpdated = createAction<{
  testId: Test['id'];
  steps: {
    added: Step[];
    modified: Partial<Step>[];
    removed: Step['id'][];
  };
  group: CurrentTestGrouping;
}>('backend/testUpdated');

const sessionPauseRequested = createAction<Omit<PlaybackCursorPosition, 'runningStep'>>(
  'backend/sessionPauseRequested',
);

const newStepsSet = createAction<Test>('backend/newStepsSet');

const setRecordingState = createAction<Test>('backend/setRecordingState');

const stepsUpdated = createAction<{
  testId: Test['id'];
  delta?: {
    added?: Step[];
    modified: Step[];
    removed: Step['id'][];
  };
}>('backend/stepsUpdated');

const stepElementScreenshotRecorded = createAction<{
  stepId: Step['id'];
  screenshot: string;
}>('backend/stepElementScreenshotRecorded');

const stepElementScreenshotUpdated = createAction<{
  stepId: Step['id'];
  screenshot: URLString;
}>('backend/stepElementScreenshotUpdated');

const setCurrentTestLoading = createAction<{
  testId: Test['id'];
  isLoading: boolean;
}>('backend/setCurrentTestLoading');

const testChanged = createAction<{
  testId: Test['id'];
  triggeredBy: {
    id: User['id'];
    email: User['email'];
    username: string;
  };
}>('backend/testChanged');

const testRunUpdated = createAction<TestRun>('backend/testRunUpdated');

const testRunUpdatedMultiple = createAction<TestRun[]>('backend/testRunUpdatedMultiple');

const testRunStopped = createAction<{ testId: Test['id']; testRunId: TestRun['id'] }>(
  'backend/testRunStopped',
);

const testRunStoppedMultiple = createAction<
  { testId: Test['id']; testRunId: TestRun['id'] }[] | undefined
>('backend/testRunStoppedMultiple');

const recordingStarted = createAction<{
  intoGroupId: Group['id'];
  afterStepId: Maybe<Step['id']>;
}>('backend/recordingStarted');

export const BackendActions = {
  testUpdated,
  sessionPauseRequested,
  recordingStarted,
  setRecordingState,
  newStepsSet,
  testRunStopped,
  testRunStoppedMultiple,
  testRunUpdated,
  testRunUpdatedMultiple,
  setCurrentTestLoading,
  testChanged,
  stepsUpdated,
  stepElementScreenshotUpdated,
  stepElementScreenshotRecorded,
} as const;
